import React, { useMemo, useState, Children } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SlidersHorizontalIcon, XIcon, ChevronDownIcon, SearchXIcon } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { ProductCard } from '../components/ProductCard';
import { Size } from '../data/products';
const CATEGORIES = ['Shirts', 'T-Shirts', 'Jeans', 'Jackets', 'Hoodies'];
const PRICE_RANGES = [{
  label: 'Under ₹999',
  min: 0,
  max: 999
}, {
  label: '₹999 - ₹1999',
  min: 999,
  max: 1999
}, {
  label: '₹1999 - ₹2999',
  min: 1999,
  max: 2999
}, {
  label: 'Above ₹2999',
  min: 2999,
  max: Infinity
}];
const SIZES: Size[] = ['S', 'M', 'L', 'XL', 'XXL'];
const SORT_OPTIONS = [{
  value: 'newest',
  label: 'Newest'
}, {
  value: 'price-low',
  label: 'Price: Low to High'
}, {
  value: 'price-high',
  label: 'Price: High to Low'
}, {
  value: 'rating',
  label: 'Highest Rated'
}, {
  value: 'popular',
  label: 'Most Popular'
}];
export function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    products,
    searchQuery
  } = useStore();
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
    const cat = searchParams.get('category');
    return cat ? [cat] : [];
  });
  const [selectedPriceRange, setSelectedPriceRange] = useState<number | null>(null);
  const [selectedSizes, setSelectedSizes] = useState<Size[]>([]);
  const [minRating, setMinRating] = useState<number>(0);
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');
  const toggleCategory = (cat: string) => {
    setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };
  const toggleSize = (size: Size) => {
    setSelectedSizes(prev => prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]);
  };
  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedPriceRange(null);
    setSelectedSizes([]);
    setMinRating(0);
  };
  const hasActiveFilters = selectedCategories.length > 0 || selectedPriceRange !== null || selectedSizes.length > 0 || minRating > 0;
  const filteredProducts = useMemo(() => {
    let result = [...products];
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q) || p.description.toLowerCase().includes(q) || p.tags.some(t => t.toLowerCase().includes(q)));
    }
    // Category filter
    if (selectedCategories.length > 0) {
      result = result.filter(p => selectedCategories.includes(p.category));
    }
    // Price filter
    if (selectedPriceRange !== null) {
      const range = PRICE_RANGES[selectedPriceRange];
      result = result.filter(p => p.sellingPrice >= range.min && p.sellingPrice <= range.max);
    }
    // Size filter
    if (selectedSizes.length > 0) {
      result = result.filter(p => selectedSizes.some(size => p.sizes[size] > 0));
    }
    // Rating filter
    if (minRating > 0) {
      result = result.filter(p => p.ratings >= minRating);
    }
    // Sort
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.sellingPrice - b.sellingPrice);
        break;
      case 'price-high':
        result.sort((a, b) => b.sellingPrice - a.sellingPrice);
        break;
      case 'rating':
        result.sort((a, b) => b.ratings - a.ratings);
        break;
      case 'popular':
        result.sort((a, b) => b.reviewCount - a.reviewCount);
        break;
      case 'newest':
        result.sort((a, b) => (b.isNewArrival ? 1 : 0) - (a.isNewArrival ? 1 : 0));
        break;
    }
    return result;
  }, [products, searchQuery, selectedCategories, selectedPriceRange, selectedSizes, minRating, sortBy]);
  const FilterContent = () => <div className="space-y-6" data-id="element-1332">
      {/* Categories */}
      <div data-id="element-1333">
        <h3 className="font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wider" data-id="element-1334">
          Category
        </h3>
        <div className="space-y-2" data-id="element-1335">
          {CATEGORIES.map(cat => <label key={cat} className="flex items-center gap-3 cursor-pointer group" data-id="element-1336">
              <input type="checkbox" checked={selectedCategories.includes(cat)} onChange={() => toggleCategory(cat)} className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" data-id="element-1337" />
              <span className="text-sm text-slate-700 group-hover:text-sky-600 transition-colors" data-id="element-1338">
                {cat}
              </span>
              <span className="text-xs text-slate-400 ml-auto" data-id="element-1339">
                ({products.filter(p => p.category === cat).length})
              </span>
            </label>)}
        </div>
      </div>

      {/* Price Range */}
      <div data-id="element-1340">
        <h3 className="font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wider" data-id="element-1341">
          Price Range
        </h3>
        <div className="space-y-2" data-id="element-1342">
          {PRICE_RANGES.map((range, i) => <label key={range.label} className="flex items-center gap-3 cursor-pointer group" data-id="element-1343">
              <input type="radio" name="price" checked={selectedPriceRange === i} onChange={() => setSelectedPriceRange(selectedPriceRange === i ? null : i)} className="w-4 h-4 border-slate-300 text-sky-600 focus:ring-sky-500" data-id="element-1344" />
              <span className="text-sm text-slate-700 group-hover:text-sky-600 transition-colors" data-id="element-1345">
                {range.label}
              </span>
            </label>)}
        </div>
      </div>

      {/* Size */}
      <div data-id="element-1346">
        <h3 className="font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wider" data-id="element-1347">
          Size
        </h3>
        <div className="flex flex-wrap gap-2" data-id="element-1348">
          {SIZES.map(size => <button key={size} onClick={() => toggleSize(size)} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedSizes.includes(size) ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-700 border-slate-200 hover:border-sky-300'}`} data-id="element-1349">
              {size}
            </button>)}
        </div>
      </div>

      {/* Rating */}
      <div data-id="element-1350">
        <h3 className="font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wider" data-id="element-1351">
          Rating
        </h3>
        <div className="space-y-2" data-id="element-1352">
          <label className="flex items-center gap-3 cursor-pointer group" data-id="element-1353">
            <input type="radio" name="rating" checked={minRating === 4} onChange={() => setMinRating(minRating === 4 ? 0 : 4)} className="w-4 h-4 border-slate-300 text-sky-600 focus:ring-sky-500" data-id="element-1354" />
            <span className="text-sm text-slate-700" data-id="element-1355">4★ & above</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer group" data-id="element-1356">
            <input type="radio" name="rating" checked={minRating === 3} onChange={() => setMinRating(minRating === 3 ? 0 : 3)} className="w-4 h-4 border-slate-300 text-sky-600 focus:ring-sky-500" data-id="element-1357" />
            <span className="text-sm text-slate-700" data-id="element-1358">3★ & above</span>
          </label>
        </div>
      </div>

      {hasActiveFilters && <button onClick={clearFilters} className="w-full py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-1359">
          Clear All Filters
        </button>}
    </div>;
  return <div className="min-h-screen bg-slate-50" data-id="element-1360">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8" data-id="element-1361">
        {/* Top Bar */}
        <div className="flex items-center justify-between mb-6" data-id="element-1362">
          <div data-id="element-1363">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-id="element-1364">
              {selectedCategories.length === 1 ? selectedCategories[0] : 'All Products'}
            </h1>
            <p className="text-sm text-slate-500 mt-1" data-id="element-1365">
              {filteredProducts.length} product
              {filteredProducts.length !== 1 ? 's' : ''} found
              {searchQuery && <span data-id="element-1366">
                  {' '}
                  for "<strong data-id="element-1367">{searchQuery}</strong>"
                </span>}
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-1368">
            {/* Mobile Filter Toggle */}
            <button onClick={() => setIsMobileFilterOpen(true)} className="lg:hidden flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:border-sky-300 transition-colors" data-id="element-1369">
              <SlidersHorizontalIcon className="w-4 h-4" data-id="element-1370" />
              Filters
              {hasActiveFilters && <span className="w-5 h-5 bg-sky-500 text-white text-xs rounded-full flex items-center justify-center" data-id="element-1371">
                  !
                </span>}
            </button>

            {/* Sort Dropdown */}
            <div className="relative" data-id="element-1372">
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="appearance-none bg-white border border-slate-200 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent cursor-pointer" data-id="element-1373">
                {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value} data-id="element-1374">
                    {opt.label}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-1375" />
            </div>
          </div>
        </div>

        <div className="flex gap-8" data-id="element-1376">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0" data-id="element-1377">
            <div className="sticky top-24 bg-white rounded-xl border border-slate-200 p-6" data-id="element-1378">
              <h2 className="font-bold text-slate-900 mb-6" data-id="element-1379">Filters</h2>
              <FilterContent data-id="element-1380" />
            </div>
          </aside>

          {/* Product Grid */}
          <div className="flex-1" data-id="element-1381">
            {filteredProducts.length > 0 ? <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-4 md:gap-6" initial="hidden" animate="visible" variants={{
            visible: {
              transition: {
                staggerChildren: 0.04
              }
            }
          }} data-id="element-1382">
                {filteredProducts.map(product => <motion.div key={product.id} variants={{
              hidden: {
                opacity: 0,
                y: 20
              },
              visible: {
                opacity: 1,
                y: 0
              }
            }} transition={{
              duration: 0.3
            }} data-id="element-1383">
                    <ProductCard product={product} data-id="element-1384" />
                  </motion.div>)}
              </motion.div> : <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-1385">
                <SearchXIcon className="w-16 h-16 text-slate-300 mb-4" data-id="element-1386" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2" data-id="element-1387">
                  No products found
                </h3>
                <p className="text-slate-500 mb-6 max-w-md" data-id="element-1388">
                  Try adjusting your filters or search terms to find what you're
                  looking for.
                </p>
                <button onClick={clearFilters} className="px-6 py-2.5 bg-sky-600 text-white font-medium rounded-lg hover:bg-sky-700 transition-colors" data-id="element-1389">
                  Clear Filters
                </button>
              </div>}
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {isMobileFilterOpen && <div className="fixed inset-0 z-50 lg:hidden" data-id="element-1390">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsMobileFilterOpen(false)} data-id="element-1391" />
          <div className="absolute top-0 right-0 bottom-0 w-4/5 max-w-sm bg-white shadow-xl flex flex-col" data-id="element-1392">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between" data-id="element-1393">
              <h2 className="font-bold text-lg text-slate-900" data-id="element-1394">Filters</h2>
              <button onClick={() => setIsMobileFilterOpen(false)} className="p-2 text-slate-500 hover:bg-slate-100 rounded-full" data-id="element-1395">
                <XIcon className="w-5 h-5" data-id="element-1396" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6" data-id="element-1397">
              <FilterContent data-id="element-1398" />
            </div>
            <div className="p-4 border-t border-slate-200" data-id="element-1399">
              <button onClick={() => setIsMobileFilterOpen(false)} className="w-full py-3 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 transition-colors" data-id="element-1400">
                Show {filteredProducts.length} Results
              </button>
            </div>
          </div>
        </div>}
    </div>;
}